import { type ComponentPropsWithoutRef, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

// ============================================================
// Overrides zudoku's default `img` MDX renderer (wired via
// `mdx.components.img` in zudoku.config.tsx) so every screenshot
// across the docs opens in a closable, zoomable modal on click --
// matching the click-to-zoom UX of GitBook/ReadMe. Zudoku itself
// has no built-in lightbox as of 0.86.0 (checked upstream source),
// so this fills the gap without forking the package.
//
// The video branch mirrors zudoku's own default `img` renderer,
// which repurposes markdown image syntax for embedded video files.
// That behavior must be preserved here since this component fully
// replaces the default one.
// ============================================================

const VIDEO_EXTENSION_PATTERN = /\.(mp4|webm|mov|avi)$/;

type ZoomableImageProps = ComponentPropsWithoutRef<"img"> & {
  node?: unknown;
};

export function ZoomableImage({ node, className, ...props }: ZoomableImageProps) {
  const [open, setOpen] = useState(false);
  const src = props.src ?? "";

  if (VIDEO_EXTENSION_PATTERN.test(src)) {
    return (
      // biome-ignore lint/a11y/useMediaCaption: source markdown provides no caption
      <video
        src={src}
        controls
        playsInline
        autoPlay
        loop
        className={`rounded-lg ${className ?? ""}`}
      />
    );
  }

  return (
    <>
      <img
        alt={props.alt}
        {...props}
        className={`rounded-lg cursor-zoom-in transition-opacity hover:opacity-90 ${className ?? ""}`}
        onClick={() => setOpen(true)}
      />
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={src ? [{ src, alt: props.alt }] : []}
        plugins={[Zoom]}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
      />
    </>
  );
}
